import { Alert, NumberInput } from '@mantine/core';
import { IconAlertTriangle, IconGauge, IconInfoCircle } from '@tabler/icons-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { StatusBadge } from '../../StatusBadge.js';
import classes from './Condition.module.css';
import type { LimitsView, Measurement, MeasurementPoint } from './condition-types.js';
import {
  dangerLabel,
  evaluate,
  limitLabel,
  measureNumber,
  resultKey,
  resultTone,
} from './condition-view.js';
import pms from './Pms.module.css';
import work from './PmsWork.module.css';
import { RowsSkeleton } from './PmsWorkParts.js';

export interface MeasurementsStepProps {
  /** `null`: okunuyor. */
  points: readonly MeasurementPoint[] | null;
  /** Değeri raporda zorunlu tetikleyici noktalar (ADR 0029 C3). */
  required: ReadonlySet<string>;
  values: Readonly<Record<string, number | ''>>;
  /** Verilmezse adım salt okunurdur (ofis, raporlanmış iş emri). */
  onChange?: (pointId: string, value: number | '') => void;
  /** Raporlanmış iş emrinin ölçümleri: limit anlık kopyasıyla salt okunur. */
  recorded?: readonly Measurement[] | null;
  /** Form hatası (`PARAMETER_REQUIRED`). */
  error?: string | undefined;
}

/** Sonuç rozeti: PASS yeşil, ABNORMAL sarı, FAIL kırmızı (README tokenları). */
export function ResultBadge({ result }: { result: string }) {
  const { t } = useTranslation();
  return (
    <StatusBadge tone={resultTone(result)}>
      {t(`pms.condition.results.${resultKey(result)}`)}
    </StatusBadge>
  );
}

function LimitCell({ limits, unit }: { limits: LimitsView; unit: string }) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';
  const danger = dangerLabel(limits, unit, language);
  return (
    <span className={classes.parameter}>
      <span>{limitLabel(limits, unit, language)}</span>
      {danger !== '—' ? (
        <span className={classes.sub}>{t('pms.condition.points.danger', { limit: danger })}</span>
      ) : null}
    </span>
  );
}

/**
 * İş emrinin Measurements adımı (PMS belgesi §11–§12; plan M7 Task 21a): işin ölçüm noktaları,
 * limitleri ve girilen değerin canlı PASS / ABNORMAL / FAIL sonucu (domain değerlendirmesi; K18).
 * FAIL ve otomatik arızası açık noktada "bir arıza kaydı açılacak" bildirimi (K19). Değerler
 * Complete & Report ile yazılır; raporlanmış iş emrinde kayıtlı ölçümler salt okunur [NR496 4.5.3f].
 */
export function MeasurementsStep({
  points,
  required,
  values,
  onChange,
  recorded,
  error,
}: MeasurementsStepProps) {
  const { t, i18n } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`pms.condition.step.${key}`, options);
  const language = i18n.resolvedLanguage ?? 'en';
  if (points === null) return <RowsSkeleton rows={4} />;

  if (recorded) {
    const byId = new Map(points.map((p) => [p.id, p]));
    return (
      <div className={classes.stack}>
        <h3 className={classes.sectionTitle}>{k('recorded')}</h3>
        {recorded.length === 0 ? (
          <p className={work.note}>{k('recordedEmpty')}</p>
        ) : (
          <MeasurementTable>
            {recorded.map((m) => (
              <tr key={m.id} className={m.voided ? classes.voided : undefined}>
                <td data-label={k('parameter')}>{byId.get(m.pointId)?.name ?? '—'}</td>
                <td data-label={k('value')} data-numeric>
                  {`${measureNumber(language, m.value)} ${m.unit}`}
                </td>
                <td data-label={k('limit')}>
                  {m.limitsSnapshot ? <LimitCell limits={m.limitsSnapshot} unit={m.unit} /> : '—'}
                </td>
                <td data-label={k('result')}>
                  <span className={work.badgeRow}>
                    <ResultBadge result={String(m.result)} />
                    {m.voided ? <StatusBadge tone="neutral">{k('voided')}</StatusBadge> : null}
                  </span>
                </td>
              </tr>
            ))}
          </MeasurementTable>
        )}
      </div>
    );
  }

  if (points.length === 0) {
    return (
      <EmptyState
        icon={<IconGauge size={22} stroke={1.8} />}
        title={k('empty')}
        description={onChange ? k('emptyHint') : undefined}
      />
    );
  }

  const results = points.map((p) => {
    const value = values[p.id];
    return { point: p, result: typeof value === 'number' ? evaluate(value, p) : null };
  });
  const failed = results.filter((r) => r.result === 'fail');
  const opens = failed.filter((r) => r.point.autoDefect).map((r) => r.point.name);
  const silent = failed.filter((r) => !r.point.autoDefect).map((r) => r.point.name);

  return (
    <div className={classes.stack}>
      <p className={work.note}>{k('hint')}</p>
      <MeasurementTable>
        {results.map(({ point, result }) => {
          const value = values[point.id] ?? '';
          return (
            <tr key={point.id}>
              <td data-label={k('parameter')}>
                <span className={classes.parameter}>
                  <strong>{point.name}</strong>
                  {required.has(point.id) ? (
                    <span className={classes.required}>{k('required')}</span>
                  ) : null}
                </span>
              </td>
              <td data-label={k('value')} className={classes.valueCell}>
                {onChange ? (
                  <NumberInput
                    size="xs"
                    aria-label={k('valueOf', { name: point.name })}
                    decimalScale={3}
                    value={value}
                    onChange={(next) => onChange(point.id, typeof next === 'number' ? next : '')}
                    rightSection={<span className={work.unit}>{point.unit}</span>}
                    rightSectionWidth={48}
                  />
                ) : value === '' ? (
                  '—'
                ) : (
                  `${measureNumber(language, value)} ${point.unit}`
                )}
              </td>
              <td data-label={k('limit')}>
                <LimitCell limits={point} unit={point.unit} />
              </td>
              <td data-label={k('result')}>{result ? <ResultBadge result={result} /> : '—'}</td>
            </tr>
          );
        })}
      </MeasurementTable>
      {error ? (
        <p className={work.error} role="alert">
          {error}
        </p>
      ) : null}
      {opens.length > 0 ? (
        <Alert color="red" icon={<IconAlertTriangle size={16} />} role="status">
          {k('defectNotice', { names: opens.join(', ') })}
        </Alert>
      ) : null}
      {silent.length > 0 ? (
        <Alert color="orange" icon={<IconInfoCircle size={16} />} role="status">
          {k('noDefect', { names: silent.join(', ') })}
        </Alert>
      ) : null}
    </div>
  );
}

function MeasurementTable({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const k = (key: string) => t(`pms.condition.step.${key}`);
  return (
    <section
      className={pms.tableBox}
      aria-label={t('pms.equipment.tableOf', { name: k('title') })}
      // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
      tabIndex={0}
    >
      <table className={pms.table} data-compact data-cards>
        <thead>
          <tr>
            <th scope="col">{k('parameter')}</th>
            <th scope="col">{k('value')}</th>
            <th scope="col">{k('limit')}</th>
            <th scope="col">{k('result')}</th>
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </section>
  );
}
